/* eslint-disable no-console */
import * as constants from '../constants/websocket';
import {
  endUserJoinedChat,
  setChatParticipantsFromEvent,
  newMessage,
  inlineMessageUpdate,
  needsActionUpdate,
} from '../actions/chat';
import { showSnackbar } from '../actions/notification';
import { addBulkUser, addUser, modifiedUser } from '../actions/users';
import { assignUser, updateJob } from '../actions/job';
import { addItem, modifyItem } from '../actions/common';
import {
  CREATE_NOTE,
  DELETE_NOTE,
  DELETE_REPLY,
  ADD_REPLY,
  NEW_MESSAGE,
  UPDATED_USER,
  REDACT_IMAGE,
} from '../constants/actions';
import { NEEDS_ATTENTION, NEEDS_ACTION } from '../constants/permissions';
import globalMediaCacheFactory from '../hooks/global-file-cache';
import { REDACTED_IMAGE } from '../constants/general';
import User from '../models/user';
import { s3toPipedUrl } from './urls';
import { JOB_RELATED_EVENTS } from './job-event-processor';
import { deauthUserAndUnsubscribeTopics } from '@app/actions/auth-utils';
import Message from '@app/models/message';
import handleChatMessageItem, {
  handleDismissNeedsAction,
  handleDismissAllNeedsAttention,
} from '@app/cache/chat-messages/chat-cache';
import {
  getWebSocketPayloadArray,
  parseEventPayload,
} from '@app/helpers/websocket-event-payload';
import { QUERY_KEYS } from '@app/constants/service';
const jobAssignedEvent = (data) => assignUser(data);
const newNoteEvent = (data) => (dispatch) =>
  dispatch({ type: CREATE_NOTE, payload: data });
const updateGroupEvent = (data) => modifyItem('GROUP')(data);
const createGroupEvent = (data) => addItem('GROUP')(data);
const deleteNoteEvent = (data) => (dispatch) =>
  dispatch({ type: DELETE_NOTE, payload: data });
const newReplyEvent = (data) => (dispatch) =>
  dispatch({ type: ADD_REPLY, payload: data });
const deleteReplyEvent = (data) => (dispatch) =>
  dispatch({ type: DELETE_REPLY, payload: data });

const newUserMentionedEvent = (data) =>
  showSnackbar({
    text: data?.noteText,
    linkText: 'View',
    isError: false,
    linkID: `/chats/${data?.caseId}`,
    title: 'You were mentioned in a note',
    isHyperLink: true,
    persist: true,
    jobId: data?.caseId,
  });

export const newJobEvent = (data, _queryClient, _eventName) => (dispatch) => {
  dispatch(addItem('CASE')(data));
  // React Query cache updates handled by job-event-processor.ts
};

const updateJobEvent = (data, _queryClient, _eventName) => (dispatch) => {
  dispatch(updateJob(data));
  // React Query cache updates handled by job-event-processor.ts
};

export const shouldSkipMessageUpdate = (lastViewedMessage, message) => {
  if (lastViewedMessage?.messageId === message?._id) {
    return true;
  }
  if (lastViewedMessage?.timestamp && message?.updatedAt) {
    const lastViewedMessageTimestamp = new Date(
      lastViewedMessage?.timestamp,
    ).getTime();
    const messageUpdateAtTimestamp = new Date(message?.updatedAt).getTime();
    if (
      Number.isNaN(lastViewedMessageTimestamp) ||
      Number.isNaN(messageUpdateAtTimestamp)
    ) {
      return false;
    }
    if (lastViewedMessageTimestamp >= messageUpdateAtTimestamp) {
      return true;
    }
  }
  return false;
};

const newMessageEvent =
  (data, queryClient, _eventName) => (dispatch, getStore) => {
    dispatch(newMessage(data));
    const store = getStore();
    handleChatMessageItem(
      data?.chatId,
      new Message(data),
      NEW_MESSAGE,
      queryClient,
      store,
    );
    // Invalidate quiet hours cache only for the active case so the
    // banner/send lock clears when the deferred welcome message is sent
    const activeJobId = store?.jobs?.activeChat?.jobId;
    if (activeJobId && data?.jobId === activeJobId) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.QUIET_HOURS_TASKS(activeJobId),
      });
    }
  };

const dismissNeedsActionEvent = (data, queryClient) => (dispatch, getStore) => {
  const { job } = data;
  const store = getStore();
  handleDismissNeedsAction(job?.primaryChatId, queryClient, store);
};

const dismissNeedsAttentionReplyEvent =
  (data, queryClient) => (dispatch, getStore) => {
    const { job } = data;
    const store = getStore();
    handleDismissAllNeedsAttention(job?.primaryChatId, queryClient, store);
  };

const dismissNeedsAttentionEvent =
  (data, queryClient) => (dispatch, getStore) => {
    const { message } = data;
    const store = getStore();
    handleChatMessageItem(message?.chatId, message, '', queryClient, store);
  };

/**
 * Function to check if we should redact the live update message event
 * @param {*} messageEvent message object coming in from event
 * @param {*} localMessage message object in redux store
 * @param {*} authId id of logged in user from redux
 * @returns {Boolean} on whether the image should be redacted
 */
export const redactImageMessageEvent = (messageEvent, authId) => {
  const redactionDataItem = messageEvent?.redactionData?.find(
    (r) => r.Type === REDACTED_IMAGE,
  );
  const createdBy = redactionDataItem?.createdBy;
  return Boolean(authId && redactionDataItem && createdBy);
};

export const updateMessageEvent =
  (data, queryClient, _eventName) => async (dispatch, getStore) => {
    const store = getStore();
    const authId = store.auth.user._id || store.auth.user.id;
    const redactImageForUser = redactImageMessageEvent(data.newMessage, authId);
    let { newMessage: updatedMessage } = data || {};
    if (redactImageForUser) {
      const { fileUrl, redactionData } = data.newMessage;
      const pipedUrl = s3toPipedUrl(fileUrl);
      const globalFileCache = await globalMediaCacheFactory();
      await globalFileCache?.deleteItem(pipedUrl);
      updatedMessage = {
        ...data.newMessage,
        fileUrl: pipedUrl,
        redactionData,
      };
      dispatch({
        type: REDACT_IMAGE,
        payload: {
          fileUrl: pipedUrl,
          messageId: updatedMessage._id,
          redactionData,
        },
      });
    }
    handleChatMessageItem(
      updatedMessage?.chatId,
      new Message(updatedMessage),
      '',
      queryClient,
      store,
    );
  };

export const inlineMessageUpdateEvent =
  (data, queryClient, _eventName) => (dispatch, getStore) => {
    const store = getStore();
    const userHasAccess = store?.auth?.permissions.includes(NEEDS_ATTENTION);
    if (userHasAccess) {
      const message = new Message(data?.message || data?.newMessage);
      handleChatMessageItem(message?.chatId, message, '', queryClient, store);
      dispatch(inlineMessageUpdate(data));
    }
  };

export const updatedNeedsActionEvent =
  (data, queryClient, _eventName) => (dispatch, getStore) => {
    const store = getStore();
    const userHasAccess = store?.auth?.permissions.includes(NEEDS_ACTION);
    if (userHasAccess) {
      const message = new Message(data?.message || data?.newMessage);
      handleChatMessageItem(message?.chatId, message, '', queryClient, store);
      dispatch(needsActionUpdate(data));
    }
  };

const handleLogoutUser = () => (dispatch) => {
  dispatch(deauthUserAndUnsubscribeTopics());
};

const handleModifiedUser = (data, queryClient) => (dispatch, getStore) => {
  const store = getStore();
  dispatch(modifyItem('OPERATOR')(data));
  dispatch(modifiedUser(data));
  handleChatMessageItem(
    data?.chatId,
    new Message(data),
    UPDATED_USER,
    queryClient,
    store,
    new User(data),
  );
};

export const handleUpdateInboxFiltersEvent =
  (data, queryClient, name, userId) => (dispatch, getStore) => {
    const store = getStore();
    if (userId !== store?.auth?.user?._id) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.USER_PROPERTIES(userId),
    });
    dispatch({
      type: 'UPDATE_PROFILE_FILTERS',
      payload: { inboxFilters: data },
    });
  };

export const handleUpdateInboxBehaviorEvent =
  (data, queryClient, name, userId) => (dispatch, getStore) => {
    const store = getStore();
    if (userId !== store?.auth?.user?._id) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.USER_PROPERTIES(userId),
    });
    dispatch({
      type: 'UPDATE_PROFILE_FILTERS',
      payload: { inboxBehavior: data },
    });
  };

const addTranslationEvent = (data, queryClient) => (dispatch, getStore) => {
  const store = getStore();
  handleChatMessageItem(
    data?.chatId,
    new Message(data),
    '',
    queryClient,
    store,
  );
};

/**
 * Get user permissions
 * @param {*} organizationName Name of users organzation
 * @param {*} userId Id of user
 * @returns {Array[string]} array of a users permissions
 */
const eventMap = {};

eventMap[constants.NEW_USER] = addUser;
eventMap[constants.NEW_BULK_USER] = addBulkUser;
eventMap[constants.UPDATED_USER] = handleModifiedUser;
eventMap[constants.NEW_MESSAGE] = newMessageEvent;
eventMap[constants.USER_ASSIGNED] = jobAssignedEvent;
eventMap[constants.ADD_TRANSLATION] = addTranslationEvent;
eventMap[constants.NEW_JOB] = newJobEvent;
eventMap[constants.UPDATED_JOB] = updateJobEvent;
eventMap[constants.CREATE_NOTE] = newNoteEvent;
eventMap[constants.UPDATE_GROUP] = updateGroupEvent;
eventMap[constants.CREATE_GROUP] = createGroupEvent;
eventMap[constants.USER_MENTIONED] = newUserMentionedEvent;
// Dynamically imported to keep papaparse out of the main bundle — this handler
// is only needed after a bulk user upload completes via websocket.
eventMap[constants.USER_OKTA_INSERTION_FINISH] =
  (data, _queryClient, _eventName, _userId) => async (dispatch) => {
    const { handleBulkUploadOktaComplete } = await import(
      '../middlewares/bulk-user-upload-handler'
    );
    await handleBulkUploadOktaComplete(data)(dispatch);
  };
eventMap[constants.DELETE_NOTE] = deleteNoteEvent;
eventMap[constants.INLINE_MESSAGE_UPDATE] = inlineMessageUpdateEvent;
eventMap[constants.UPDATED_NEEDS_ACTION] = updatedNeedsActionEvent;
eventMap[constants.UPDATE_MESSAGE] = updateMessageEvent;
eventMap[constants.CHAT_END_USER_REMOVED] = setChatParticipantsFromEvent;
eventMap[constants.CHAT_END_USER_JOINED] = endUserJoinedChat;
eventMap[constants.ADD_REPLY] = newReplyEvent;
eventMap[constants.DELETE_REPLY] = deleteReplyEvent;
eventMap[constants.LOGOUT_USER] = handleLogoutUser;
eventMap[constants.UPDATE_INBOX_FILTERS] = handleUpdateInboxFiltersEvent;
eventMap[constants.UPDATE_INBOX_BEHAVIOR] = handleUpdateInboxBehaviorEvent;
eventMap[constants.DISMISS_NEEDS_ACTION] = dismissNeedsActionEvent;
eventMap[constants.DISMISS_NEEDS_ATTENTION_REPLY] =
  dismissNeedsAttentionReplyEvent;
eventMap[constants.DISMISS_NEEDS_ATTENTION] = dismissNeedsAttentionEvent;
eventMap[constants.SCHEDULED_MESSAGE_CREATED] = (data) => (dispatch) => {
  dispatch({ type: 'SCHEDULED_MESSAGE_CREATED', payload: data });
};
eventMap[constants.SCHEDULED_MESSAGE_CANCELLED] = (data) => (dispatch) => {
  dispatch({ type: 'SCHEDULED_MESSAGE_CANCELLED', payload: data });
};

export const eventProcessor = (dispatch, queryClient) => (messageEvent) => {
  const payload = getWebSocketPayloadArray(messageEvent);
  if (!payload) return;
  payload.forEach((event) => {
    const eventHandler = eventMap[event.name];
    if (eventHandler) {
      try {
        const data = parseEventPayload(event.payload);
        dispatch(eventHandler(data, queryClient, event.name, event?.userId));
      } catch (err) {
        console.error('cannot parse payload: ', event.payload);
        console.error(err);
      }
    } else {
      // Only log unknown events that aren't handled by job-event-processor.ts
      if (!JOB_RELATED_EVENTS.has(event.name)) {
        console.error('cannot find event handler for: ', event);
      }
    }
  });
};
